import { createWebHistory, createRouter } from 'vue-router'
import { useAuthStore } from '@/stores/AuthStore'
import { ADMIN_ROLE, RESTAURANT_ROLE } from '@/utils/ConstVal.js'

// 全局路由守卫：每次导航前集中处理登录状态、角色权限和餐厅 ruid 的约束
const requireAuth = async (to, from, next) => {
    const requiresAuth = to.matched.some((record) => record.meta?.requiresAuth)

    if (!requiresAuth) {
        return next()
    }

    const authStore = useAuthStore()
    const currentUser = await authStore.getCurrentUser()

    if (!currentUser) {
        return next({ name: 'Login' })
    }

    const claims = currentUser.claims ?? {}
    const role = claims.role
    const ruid = claims.ruid

    // 汇总被匹配到的所有路由记录上的 allowedRoles，确保父子路由共同限制角色
    const allowedRoles = to.matched
        .map((record) => record.meta?.allowedRoles)
        .filter(Boolean)
        .flat()

    // 如果当前路由对角色有限制，而用户 role 为空或不在允许列表中，则直接拒绝访问
    if (allowedRoles.length && (!role || !allowedRoles.includes(role))) {
        return next({ name: '404' })
    }

    // enforceOwnRuid 表示该路由仅允许餐厅角色访问自己的店铺数据
    const requiresOwnRuid = to.matched.some((record) => record.meta?.enforceOwnRuid)

    if (requiresOwnRuid && role !== ADMIN_ROLE) {
        if (!ruid) {
            return next({ name: '404' })
        }

        const routeRuid = to.params?.ruid

        if (routeRuid !== ruid) {
            return next({
                name: 'RestaurantWebsiteEdit',
                params: { ruid },
                query: to.query,
                hash: to.hash
            })
        }
    }

    return next()
}

const routes = [
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/LoginView.vue')
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPassword.vue')
    },
    {
        path: '/auth/action',
        name: 'ResetPassword',
        component: () => import('@/views/ResetPassword.vue')
    },
    {
        path: '/',
        name: 'Home',
        redirect: { name: 'Login' }
    },
    {
        path: '/website/:ruid',
        name: 'RestaurantLayout',
        meta: {
            // requiresAuth：需要登录；allowedRoles：限定角色；enforceOwnRuid：餐厅用户只能访问自己的 ruid
            requiresAuth: true,
            allowedRoles: [ADMIN_ROLE, RESTAURANT_ROLE],
            enforceOwnRuid: true
        },
        component: () => import('@/layouts/RestaurantLayout.vue'),
        children: [
            {
                path: '',
                name: 'RestaurantWebsiteEdit',
                component: () => import('@/views/RWebsiteView.vue')
            }
        ]
    },
    {
        path: '/admin',
        name: 'AdminLayout',
        meta: {
            // 仅允许管理员角色进入后台管理页面
            requiresAuth: true,
            allowedRoles: [ADMIN_ROLE]
        },
        component: () => import('@/layouts/AdminLayout.vue'),
        children: [
            {
                path: '',
                name: 'AdminHome',
                component: () => import('@/views/AHome.vue')
            },
            {
                path: 'user',
                name: 'AdminUser',
                children: [
                    {
                        path: 'create',
                        name: 'AdminUserCreate',
                        component: () => import('@/views/AUserCreate.vue')
                    },
                    {
                        path: 'list',
                        name: 'AdminUserList',
                        component: () => import('@/views/AUserList.vue')
                    },
                    {
                        path: 'detail/:uid',
                        name: 'AUserDetail',
                        component: () => import('@/views/AUserDetail.vue')
                    }
                ]
            },
            {
                path: 'admin',
                name: 'AdminAdmin',
                children: [
                    {
                        path: 'list',
                        name: 'AdminAdminList',
                        component: () => import('@/views/AAdminList.vue')
                    }
                ]
            },
            {
                path: 'restaurant',
                name: 'AdminRestaurant',
                children: [
                    {
                        path: 'create',
                        name: 'AdminRestaurantCreate',
                        component: () => import('@/views/ARestaurantCreate.vue')
                    },
                    {
                        path: 'list',
                        name: 'AdminRestaurantList',
                        component: () => import('@/views/ARestaurantList.vue')
                    },
                    {
                        path: 'detail/:ruid',
                        name: 'ARestaurantServiceConfig',
                        component: () => import('@/views/ARestaurantServiceConfig.vue')
                        // props: true
                    },
                    {
                        path: 'website/update/:ruid',
                        name: 'AdminRestaurantWebsiteUpdate',
                        component: () => import('@/views/RWebsiteView.vue')
                    }
                ]
            },
            {
                path: 'billing',
                name: 'AdminBilling',
                children: [
                    {
                        path: 'usage-based',
                        name: 'AdminBillingUsageBased',
                        component: () => import('@/views/ABillingUsageBased.vue')
                    }
                ]
            }
        ]
    },
    {
        path: '/404',
        name: '404',
        component: () => import('@/views/NotFound.vue')
    },
    {
        path: '/:catchAll(.*)',
        name: 'NotFound',
        component: () => import('@/views/NotFound.vue')
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

// 注册全局前置守卫，实现统一的认证/授权入口
router.beforeEach(requireAuth)

export default router
